import debounce from 'lodash/debounce';
import delegate from '../utils/delegate-event';
import { get } from '../utils/options';
import { navigate } from './featured-content/featured-content-service';
import { FeaturedContent } from './featured-content';

// The following is heavily based on:
// https://css-tricks.com/container-adapting-tabs-with-more-button/

const classNames = {
  quicklinks: 'c-featured-quicklinks'
};
classNames.secondary = `${classNames.quicklinks}-secondary`;
classNames.showSecondary = `${classNames.quicklinks}--show-secondary`;
classNames.expanded = `${classNames.quicklinks}--expanded`;
classNames.moreItem = `${classNames.quicklinks}-item--more`;
classNames.jsified = `${classNames.quicklinks}--js`;

class FeaturedQuicklinks {
  container;
  allItems;
  primary;
  primaryItems;
  secondary;
  secondaryItems;
  moreLi;
  moreBtn;

  constructor($el) {
    this.container = $el;
    this.primary = $el.querySelector('ul')
    this.primaryItems = this.primary.querySelectorAll(`li:not(${classNames.moreItem})`);

    $el.classList.add(classNames.jsified);

    $el.insertAdjacentHTML('beforeend', `
      <ul class="${classNames.secondary}">
        ${this.primary.innerHTML}
      </ul>
    `);
    this.primary.insertAdjacentHTML('beforeend', `
      <li class="${classNames.moreItem}">
        <button type="button" aria-haspopup="true" aria-expanded="false">
          ${get('more')}
        </button>
      </li>
    `);

    this.secondary = $el.querySelector(`.${classNames.secondary}`);
    this.secondaryItems = this.secondary.querySelectorAll('li');
    this.allItems = $el.querySelectorAll('li');
    this.moreLi = this.primary.querySelector(`.${classNames.moreItem}`);

    this.moreBtn = this.moreLi.querySelector('button');
    this.moreBtn.addEventListener('click', e => {
      e.preventDefault();
      const expanded = !$el.classList.contains(classNames.showSecondary);
      $el.classList.toggle(classNames.showSecondary, expanded);
      this.moreBtn.setAttribute('aria-expanded', expanded);
      this.moreBtn.textContent = get(expanded ? 'less' : 'more');
    });

    const mobileMedia = matchMedia('screen and (max-width: 640px)');

    // Debounce onresize, but aim for 60fps
    window.addEventListener('resize', debounce(() => mobileMedia.matches ? this.unadapt() : this.adapt(), 1000 / 60));

    // Expand/collapse in mobile view
    const expandHandler = e => {
      if (e.currentTarget === this.primary) {
        this.primary.classList.toggle(classNames.expanded);
      }
    };

    // Setup initial view
    if (mobileMedia.matches) {
      this.unadapt();
      this.primary.addEventListener('click', expandHandler);
    }
    else {
      this.adapt();
    }

    // Switch over between mobile & non-mobile views
    mobileMedia.addEventListener('change', ({ matches }) => {
      if (matches) {
        this.adapt();
        this.primary.addEventListener('click', expandHandler);
      }
      else {
        this.unadapt();
        this.primary.removeEventListener('click', expandHandler);
        this.primary.classList.remove(classNames.expanded);
      }
    });

    if ($el.dataset.contentCentrePage === 'true') {
      delegate($el, 'click', `ul > li:not(${classNames.moreItem}) > a`, e => {
        const pageSize = FeaturedContent.pageSize();
        FeaturedContent.hide();

        navigate({
          id: e.delegatedTarget.dataset.id,
          page: 1,
          pageSize,
          type: 'category',
        });
        this.primary.classList.remove(classNames.expanded);
        e.preventDefault();
      });
    }
  }

  getMoreBtnWidth() {
    const currentText = this.moreBtn.textContent;
    this.moreLi.style.display = '';

    this.moreBtn.textContent = get('more');
    const widthMore = this.moreLi.offsetWidth;
    this.moreBtn.textContent = get('less');
    const widthLess = this.moreLi.offsetWidth;

    this.moreBtn.textContent = currentText;
    return Math.max(widthLess, widthMore);
  }

  unadapt() {
    this.allItems.forEach(item => item.style.display = '');
  }

  adapt() {
    this.unadapt();

    const primaryGap = parseInt(getComputedStyle(this.primary).gap, 10) || 0;
    let stopWidth = this.getMoreBtnWidth() + primaryGap;
    let hiddenIndex = -1;
    const primaryWidth = this.primary.offsetWidth;

    for (let i = 0; i < this.primaryItems.length; i++) {
      const item = this.primaryItems[i];
      const itemWidth = item.offsetWidth + (i ? primaryGap : 0);

      if (primaryWidth >= stopWidth + itemWidth) {
        stopWidth += itemWidth;
      }
      else {
        hiddenIndex = i;
        break;
      }
    }

    if (hiddenIndex === -1) {
      this.moreLi.style.display = 'none';
      this.container.classList.remove(classNames.showSecondary);
      this.moreBtn.setAttribute('aria-expanded', false);
      this.moreBtn.textContent = get('more');
    }
    else {
      // Hide items from the primary list that don't fit
      for (let i = hiddenIndex; i < this.primaryItems.length; i++) {
        this.primaryItems[i].style.display = 'none';
      }

      // Hide items from the secondary that are in the primary list
      for (let i = 0; i < Math.min(hiddenIndex, this.secondaryItems.length); i++) {
        this.secondaryItems[i].style.display = 'none';
      }
    }
  }
}

document.querySelectorAll(`.${classNames.quicklinks}`).forEach(
  $el => new FeaturedQuicklinks($el)
);
